import type { Language } from "@helpers/I18nHelper";
import PublicLayout from "@modules/layout/components/PublicLayout";
import UserLoginLayout from "@modules/layout/components/UserLoginLayout";
import ErrorPage from "@modules/layout/error/ErrorPage";
import NotFound from "@modules/layout/notFound/NotFound";
import Layout from "@modules/layout/pages/Layout";
import type { RouteObject } from "react-router";
import { getPrivateRoutes } from "./PrivateRoutes";
import getPublicRoutes from "./PublicRoutes";

/**
 * Returns the route hierarchy for the application.
 * IMPORTANT: To fix language transition bugs, the router must statically contain
 * the paths for ALL supported languages. This prevents "no match" errors when
 * navigating between /blog and /en/blogs.
 */
const getAppRoutesHierarchy = (): RouteObject[] => {
  const languages: Language[] = ["fr", "en"];

  const allPublicRoutes = languages.flatMap((lang) => getPublicRoutes(lang));
  const allPrivateRoutes = languages.flatMap((lang) => getPrivateRoutes(lang));

  return [
    {
      element: <Layout />,
      errorElement: <ErrorPage />,
      path: "/",
      children: [
        {
          element: <PublicLayout />,
          children: allPublicRoutes,
        },
        {
          element: <UserLoginLayout />,
          children: allPrivateRoutes,
        },
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];
};

/**
 * This is the main route configuration used by createBrowserRouter.
 */
export const buildFullRouteTree = (
  AppElement: React.ReactNode,
): RouteObject[] => [
  {
    path: "/",
    element: AppElement,
    children: getAppRoutesHierarchy(),
  },
];
